import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { Automations } from '../../../../extViewmodels/Utils';
import { AdvancedTagFilter, AdvancedTagFilterCollection } from '../../../../models';
import { useAllClassifiedContactPolicies } from '../../../../queries/Contact';
import { useAutomationEditorContext } from '../../../containers/EditAutomationTemplate/hooks';
import { baseStyleSheet } from '../../../styles/styles';
import { IAutoCompleteSearchFieldComponent } from '../../autocomplete/AutoCompleteSearchField';
import {
	ISimpleAutoCompleteSearchFieldEvent,
	ISimpleAutoCompleteSearchFieldItemSelectionEvent,
	SimpleAutoCompleteSearchField,
} from '../../autocomplete/SimpleAutoCompleteSearchField';
import { DeprecatedSelect, ISelectOption } from '../../DeprecatedSelect';
import { PolicySelector } from '../../email/PolicySelector';
import { TagListBuilder } from '../../entities/tags/TagListBuilder';
import { styleSheet } from './styles';

interface IEditAutomationSwitchStepCaseStatement {
	criteria?: Api.IContactFilterCriteria;
	onCriteriaChanged?(criteria?: Api.IContactFilterCriteria): void;
	switchStep: Api.ISwitchAutomationStep;
}

function getSelectedPoliciesFromFilterRequest(filterRequest?: Api.IContactsFilterRequest): string[] {
	if (!filterRequest) {
		return [];
	}
	const onlyOneValueSelected = !filterRequest.criteria || !filterRequest.criteria?.length;

	if (filterRequest?.value && onlyOneValueSelected) {
		return [filterRequest.value];
	} else if (
		filterRequest.criteria?.length &&
		filterRequest.criteria.every(y => y.property === Api.ContactFilterCriteriaProperty.Policy)
	) {
		return filterRequest.criteria.filter(x => x.value !== undefined).map(x => x.value);
	}
	return [];
}

const getEditAutomationSwitchStepCaseStatementSelectOptions = (
	trigger?: Api.IAutomationTrigger,
	includeDefaultSegmentOption = false
) => {
	const options: ISelectOption<Api.IContactFilterCriteria>[] = [];
	if (Automations.triggers.isResourceSelectorTrigger(trigger, Api.ResourceSelectorId.PolicyRenew)) {
		options.push({
			dataContext: {
				criteria: [],
				op: Api.FilterOperator.Or,
				property: Api.ContactFilterCriteriaProperty.Policy,
			},
			id: Api.ResourceSelectorId.PolicyRenew,
			text: 'this policy renewing is',
		});
	}

	options.push({
		dataContext: {
			op: Api.FilterOperator.And,
			property: Api.ContactFilterCriteriaProperty.Tag,
		},
		id: 'tag-and',
		text: 'contact has this tag',
	});

	options.push({
		dataContext: {
			op: Api.FilterOperator.Not,
			property: Api.ContactFilterCriteriaProperty.Tag,
		},
		id: 'tag-not',
		text: 'contact is missing this tag',
	});

	if (includeDefaultSegmentOption) {
		options.push({
			dataContext: null,
			id: 'default-case',
			text: 'contact is not in a segment (everyone else)',
		});
	}
	return options;
};

interface IEditAutomationSegmentPrimaryFilterProps extends IEditAutomationSwitchStepCaseStatement {
	caseStatement: Api.IAutomationStepCaseStatement;
	filterOperatorSelectBoxStyles?: StyleDeclarationValue[];
	styles?: StyleDeclarationValue[];
	impersonationContext?: Api.IImpersonationContext;
}

export const EditAutomationSegmentPrimaryFilter: React.FC<IEditAutomationSegmentPrimaryFilterProps> = props => {
	const {
		criteria,
		onCriteriaChanged,
		filterOperatorSelectBoxStyles,
		caseStatement,
		styles = [],
		impersonationContext,
	} = props;
	const { automationTemplateTrigger } = useAutomationEditorContext();
	const trigger = automationTemplateTrigger;

	const autocompleteInputRef = React.useRef<HTMLInputElement>(null);
	const autocompleteComponentRef = React.useRef<IAutoCompleteSearchFieldComponent>(null);
	const onAutocompleteComponentRef = React.useCallback((ref?: IAutoCompleteSearchFieldComponent) => {
		autocompleteComponentRef.current = ref;
	}, []);

	const options = React.useMemo(
		() => getEditAutomationSwitchStepCaseStatementSelectOptions(trigger, !!caseStatement?.isDefault),
		[caseStatement?.isDefault, trigger]
	);

	const resolveSelectedOption = React.useCallback(
		(nextCriteria?: Api.IContactFilterCriteria) => {
			const exact = options.find(
				x =>
					x.dataContext?.property === nextCriteria?.property &&
					(x.dataContext?.op === nextCriteria?.op || !nextCriteria?.op)
			);
			if (exact) {
				return exact;
			}

			if (nextCriteria?.property === Api.ContactFilterCriteriaProperty.Tag) {
				const tagOption = options.find(x => x.dataContext?.property === Api.ContactFilterCriteriaProperty.Tag);
				if (tagOption) {
					return tagOption;
				}
			}

			if (nextCriteria?.property === Api.ContactFilterCriteriaProperty.Policy) {
				const policyOption = options.find(x => x.dataContext?.property === Api.ContactFilterCriteriaProperty.Policy);
				if (policyOption) {
					return policyOption;
				}
			}

			return options.find(x => x.dataContext === null) || options[0];
		},
		[options]
	);

	const [selectedOption, setSelectedOption] = React.useState<ISelectOption<Api.IContactFilterCriteria>>(() =>
		resolveSelectedOption(criteria)
	);

	React.useEffect(() => {
		setSelectedOption(resolveSelectedOption(criteria));
	}, [criteria, resolveSelectedOption]);

	const selectedPolicies = React.useMemo(() => getSelectedPoliciesFromFilterRequest(criteria), [criteria]);

	const { data: allClassifiedPolicies, isPending: isLoadingAllClassifiedPolicies } = useAllClassifiedContactPolicies({
		contactsRequest: { criteria: [] },
		refetchOnWindowFocus: false,
		impersonationContext,
	});

	const onSelectOptionClicked = React.useCallback(
		(option: ISelectOption<Api.IContactFilterCriteria>, wasSelected: boolean) => {
			if (wasSelected) {
				const propertyChanged = option.dataContext?.property !== criteria?.property;
				const opChanged = option.dataContext?.op !== criteria?.op;
				if (propertyChanged || opChanged) {
					autocompleteComponentRef.current?.clearInput();
				}

				setSelectedOption(option);
				onCriteriaChanged?.(
					option.dataContext
						? {
								...(option.dataContext || {}),
							}
						: null
				);
			}
		},
		[onCriteriaChanged, criteria]
	);

	const isPolicyTrigger = Automations.triggers.isResourceSelectorTrigger(trigger, Api.ResourceSelectorId.PolicyRenew);
	const autocompleteType =
		isPolicyTrigger &&
		(criteria?.property === Api.ContactFilterCriteriaProperty.Policy ||
			selectedOption?.id === Api.ResourceSelectorId.PolicyRenew)
			? Api.ResourceAutoCompleteViewModelType.LineOfBusiness
			: Api.ResourceAutoCompleteViewModelType.Tag;

	const onSearchFieldItemSelected = React.useCallback(
		(e: ISimpleAutoCompleteSearchFieldItemSelectionEvent<string | Api.IEntity | Api.IUser>) => {
			if (!e.selection || typeof e.selection !== 'string') {
				return;
			}

			const value = e.selection;
			if (e.target) {
				e.target.setSearchQuery(value);
			}

			onCriteriaChanged?.({
				...(selectedOption?.dataContext || {}),
				property: Api.ContactFilterCriteriaProperty.Tag,
				value: value || '',
			});
		},
		[onCriteriaChanged, selectedOption?.dataContext]
	);

	const onSearchFieldClearClicked = React.useCallback(() => {
		onCriteriaChanged?.({
			...(selectedOption?.dataContext || {}),
			property: Api.ContactFilterCriteriaProperty.Tag,
			value: '',
		});
	}, [onCriteriaChanged, selectedOption?.dataContext]);

	const onSearchFieldBlur = React.useCallback(
		(e: ISimpleAutoCompleteSearchFieldEvent<React.FocusEvent<HTMLInputElement>>) => {
			onCriteriaChanged?.({
				...(selectedOption?.dataContext || {}),
				property: Api.ContactFilterCriteriaProperty.Tag,
				value: e.sourceEvent?.target?.value || '',
			});
		},
		[onCriteriaChanged, selectedOption?.dataContext]
	);

	const onCreateAutoCompleteViewModel = React.useCallback(
		(_type: Api.ResourceAutoCompleteViewModelType, suggestedVm: Api.ResourceAutoCompleteViewModel) => {
			return impersonationContext ? suggestedVm.impersonate(impersonationContext) : suggestedVm;
		},
		[impersonationContext]
	);
	const onRenewalPoliciesChanged = React.useCallback(
		(policies: string[]) => {
			const nextRequest: Api.IContactsFilterRequest = policies.length
				? {
						criteria: policies.map(x => {
							return {
								property: Api.ContactFilterCriteriaProperty.Policy,
								value: x,
							};
						}),
						property: Api.ContactFilterCriteriaProperty.Policy,
						op: Api.FilterOperator.Or,
					}
				: {
						criteria: [],
					};
			onCriteriaChanged?.(nextRequest);
		},
		[onCriteriaChanged]
	);

	const searchFieldStyle = React.useRef([styleSheet.filterSearchField]).current;
	const placeholder = !criteria?.property
		? 'Search'
		: `Search ${criteria.property === Api.ContactFilterCriteriaProperty.Policy ? 'policy' : 'tags'}`;

	const primaryFilterStylesAlt = React.useRef<StyleDeclarationValue[]>([styleSheet.primaryFilterAlt]).current;

	return (
		<div
			className={css(
				baseStyleSheet.horizontalStack,
				criteria && autocompleteType === Api.ResourceAutoCompleteViewModelType.LineOfBusiness
					? [...styles]
					: [...primaryFilterStylesAlt]
			)}
		>
			{caseStatement?.isDefault ? (
				<div className={css(...(filterOperatorSelectBoxStyles || []))}>{selectedOption?.text}</div>
			) : (
				<DeprecatedSelect
					disabled={!!caseStatement?.isDefault}
					onOptionClick={onSelectOptionClicked}
					options={options}
					selectedOption={selectedOption}
					showCaret={!caseStatement?.isDefault}
					styles={filterOperatorSelectBoxStyles}
				/>
			)}
			{criteria && autocompleteType === Api.ResourceAutoCompleteViewModelType.LineOfBusiness ? (
				<PolicySelector
					classifiedPolicies={allClassifiedPolicies}
					isLoadingClassifiedPolicies={isLoadingAllClassifiedPolicies}
					onSelectedPoliciesChanged={onRenewalPoliciesChanged}
					selectedPolicies={selectedPolicies}
				/>
			) : criteria ? (
				<SimpleAutoCompleteSearchField
					initialSearchQuery={criteria?.value || undefined}
					onBlur={onSearchFieldBlur}
					onClear={onSearchFieldClearClicked}
					onCreateAutoCompleteViewModel={onCreateAutoCompleteViewModel}
					onInputRef={autocompleteInputRef}
					onItemSelected={onSearchFieldItemSelected}
					onSearchFieldRef={onAutocompleteComponentRef}
					placeholder={placeholder}
					style={searchFieldStyle}
					type={autocompleteType}
				/>
			) : null}
		</div>
	);
};

interface IEditAutomationSegmentAdditionalFiltersProps {
	initialCriteria?: Api.IContactFilterCriteria[];
	onCriteriaChanged?(criteria: Api.IContactFilterCriteria[]): void;
	impersonationContext?: Api.IImpersonationContext;
}

const filterOperatorSelectOptions: ISelectOption<Api.FilterOperator>[] = [
	{
		dataContext: Api.FilterOperator.And,
		id: 'filter-option-and',
		text: 'And contact has this tag',
	},
	{
		dataContext: Api.FilterOperator.Or,
		id: 'filter-option-or',
		text: 'And contact has at least one of these tags',
	},
];

export const EditAutomationSegmentAdditionalFilters: React.FC<IEditAutomationSegmentAdditionalFiltersProps> = props => {
	const { onCriteriaChanged, initialCriteria, impersonationContext } = props;
	const filterCollection = React.useRef<AdvancedTagFilterCollection>(
		initialCriteria
			? AdvancedTagFilterCollection.instanceWithCriteria(initialCriteria)
			: AdvancedTagFilterCollection.instanceWithFilters([])
	).current;

	const [filterOperator, setFilterOperator] = React.useState<Api.FilterOperator.And | Api.FilterOperator.Or>(() => {
		if (initialCriteria) {
			return initialCriteria.find(x => x.op !== Api.FilterOperator.Not)?.op as
				| Api.FilterOperator.And
				| Api.FilterOperator.Or;
		}
		return null;
	});

	const getFilters = React.useCallback(
		(type: Api.FilterOperator) => {
			return filterCollection.filters.filter(filter => {
				return filter.type === type;
			});
		},
		[filterCollection]
	);

	const getTagNames = React.useCallback(
		(type: Api.FilterOperator) => {
			return getFilters(type).map(f => f.tag);
		},
		[getFilters]
	);

	const onSetFilterOperatorOption = React.useCallback(
		(option: ISelectOption<Api.FilterOperator.And | Api.FilterOperator.Or>, wasSelected: boolean) => {
			if (wasSelected) {
				setFilterOperator(option.dataContext);
			}
		},
		[]
	);

	const notifyCriteriaChanged = React.useCallback(() => {
		if (onCriteriaChanged) {
			const criteriaCollection: Api.IContactFilterCriteria[] = [];

			if (filterOperator !== null && filterOperator !== undefined) {
				criteriaCollection.push({
					criteria: getFilters(filterOperator).map<Api.IContactFilterCriteria>(x => {
						const criteria = x.toContactFilterCriteria();
						delete criteria.op;
						return criteria;
					}),
					op: filterOperator,
					property: Api.ContactFilterCriteriaProperty.Tag,
				});
			}

			const notFilters = getFilters(Api.FilterOperator.Not);
			if (notFilters?.length > 0) {
				notFilters.forEach(x => criteriaCollection.push(x.toContactFilterCriteria()));
			}

			onCriteriaChanged(criteriaCollection);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterCollection, filterOperator, getFilters, onCriteriaChanged]);

	const onTagAdded = React.useCallback(
		(tag: string, type: Api.FilterOperator) => {
			if (!filterCollection.hasValue(tag)) {
				const filter = new AdvancedTagFilter();
				filter.tag = tag;
				filter.type = type;
				filterCollection.add(filter);
				notifyCriteriaChanged();
			} else {
				toast.error(`"${tag}" is already included as part of the filters.`);
			}
		},
		[filterCollection, notifyCriteriaChanged]
	);

	const onTagRemoved = React.useCallback(
		(tag: string) => {
			const filter = filterCollection.filters.find(f => f.tag === tag);
			if (filter) {
				filterCollection.remove(filter);
				notifyCriteriaChanged();
			}
		},
		[filterCollection, notifyCriteriaChanged]
	);

	const filterOperatorSelectTitle = React.useMemo(() => {
		return filterOperator === null || filterOperator === undefined ? (
			<div>Please select...</div>
		) : (
			<div>{filterOperatorSelectOptions.find(opt => opt.dataContext === filterOperator)?.text}</div>
		);
	}, [filterOperator]);

	return (
		<div>
			<div className={css(styleSheet.tagFiltersLabel)}>Additional filters</div>
			<DeprecatedSelect
				onOptionClick={onSetFilterOperatorOption}
				options={filterOperatorSelectOptions}
				selectedOptionTitle={filterOperatorSelectTitle}
				styles={[styleSheet.tagFiltersInput]}
			/>
			{filterOperator !== null && filterOperator !== undefined && (
				<TagListBuilder
					tags={getTagNames(filterOperator)}
					separator={filterOperator}
					onTagAdded={tag => onTagAdded(tag, filterOperator)}
					onTagRemoved={onTagRemoved}
					impersonationContext={impersonationContext}
				/>
			)}
			<div className={css(styleSheet.tagFiltersLabel)}>But exclude contacts that have...</div>
			<TagListBuilder
				tags={getTagNames(Api.FilterOperator.Not)}
				separator={Api.FilterOperator.Not}
				onTagAdded={tag => onTagAdded(tag, Api.FilterOperator.Not)}
				onTagRemoved={onTagRemoved}
				impersonationContext={impersonationContext}
			/>
		</div>
	);
};
